import React, { useState, useEffect } from 'react';
import { SubscriptionStatus } from '../../types/api';
import { Subscription } from '../../types/api';
import { Ellipsis, Calendar, CalendarSync, Pause, Play, ChevronLast, ChevronRight, Flower, Heart } from 'lucide-react';
import PaymentBillingSection from './PaymentBillingSection';
import SubscriptionActions from './SubscriptionActions';
import { SubscriptionState } from '../../types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FEATURES } from '../../config';
import { PreferencesModal } from './';
import { Z_INDEX, getZIndexClass } from '../../utils/zIndexUtils';

interface QuickActionsProps {
  subscription: Subscription;
  onSkip: () => void;
  onEditSchedule: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onUpgrade?: () => void;
  onRenew?: () => void;
  status?: SubscriptionStatus | null;
  setIsModalOpen: (isOpen: boolean) => void;
  activeSubscription: SubscriptionState;
}

interface Delivery {
  status: 'scheduled' | 'delivered' | 'skipped' | 'processing' | 'cancelled';
}

const QuickActions: React.FC<QuickActionsProps> = ({
  subscription,
  onSkip,
  onEditSchedule,
  onPause,
  onResume,
  onCancel,
  onUpgrade,
  onRenew,
  status,
  setIsModalOpen,
  activeSubscription,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setLocalModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  
  // Use status directly instead of keeping it in state
  // This ensures we always render with the latest props
  console.log('QuickActions: Rendering with props:', {
    id: subscription.id,
    subscriptionStatus: subscription.status,
    propStatus: status,
    activeStatus: activeSubscription.status
  });
  
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setLocalModalOpen(false);
      setIsClosing(false);
    }, 300); // Match this with animation duration
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle preferences - now opens modal instead of navigating
  const handlePreferences = () => {
    // Close the quick actions modal before showing preferences
    handleClose();
    
    // Show preferences modal
    setShowPreferencesModal(true);
  };

  // Determine if the subscription is active or paused
  const isActive = status === 'active';
  const isPaused = status === 'paused';
  const isPendingRenewal = status === 'pending_renewal';

  // For pending renewal subscriptions, only allow renewal and cancel actions
  const canSkip = isActive && !isPendingRenewal;
  const canEditSchedule = isActive && !isPendingRenewal;
  const canPauseResume = (isActive || isPaused) && !isPendingRenewal;
  const canShowMoreOptions = (isActive || isPendingRenewal || isPaused);

  return (
    <>
      <div className="rounded-lg px-4 py-2">
        {/* <h2 className="text-lg font-medium mb-4">Quick Actions</h2> */}
        
        <div className="grid grid-cols-4">
          {/* Skip Delivery */}
          <div className='flex flex-col items-center justify-center space-y-2'>
          <button 
            onClick={onSkip}
            disabled={!canSkip}
            className={`px-full py-3 border-none rounded-xl flex flex-col items-center justify-center text-center transition˚
              ${canSkip ? 'bg-amber-200/50' : 'bg-gray-100 cursor-not-allowed text-gray-400'}`}
          >
            <ChevronLast className={`mb-0 ${canSkip ? 'text-amber-500' : 'text-gray-400'} w-8 h-8`} />
          </button>
          <span className='text-sm'>{t('components.quickActions.skip')}</span>
          </div>

          {/* Edit Schedule */}
          <div className='flex flex-col items-center justify-center space-y-2'>
          <button 
            onClick={onEditSchedule}
            disabled={!canEditSchedule}
            className={`px-full py-3 border-none rounded-xl flex flex-col items-center justify-center text-center transition
              ${canEditSchedule ? 'bg-amber-200/50' : 'bg-gray-100 cursor-not-allowed text-gray-400'}`}
          >
            <Calendar className={`mb-0 ${canEditSchedule ? 'text-amber-500' : 'text-gray-400'} w-8 h-8`} />
          </button>
          <span className='text-sm'>{t('components.quickActions.schedule')}</span>
          </div>

          {/* Pause or Resume Subscription */}
          {isPaused ? (
            <div className='flex flex-col items-center justify-center space-y-2'>
            <button 
              key={`resume-${status}`}
              onClick={onResume}
              className="px-full py-3 border-none rounded-xl flex flex-col items-center justify-center text-center transition bg-amber-200"
            >
              <Play className='mb-0 text-amber-500 w-8 h-8'/>
            </button>
            <span className='text-sm'>{t('components.quickActions.resume')}</span>
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center space-y-2'>
            <button 
              key={`pause-${status}`}
              onClick={onPause}
              disabled={!canPauseResume}
              className={`px-full py-3 border-none rounded-xl flex flex-col items-center justify-center text-center transition
                ${canPauseResume ? 'bg-amber-200/50' : 'bg-gray-100 cursor-not-allowed text-gray-400'}`}
            >
              <Pause className={`mb-0 ${canPauseResume ? 'text-amber-500' : 'text-gray-400'} w-8 h-8`}/>
            </button>
            <span className='text-sm'>{t('components.quickActions.pause')}</span>
            </div>
          )}

          {/* more option  */}
          <div className='flex flex-col items-center justify-center space-y-2'>
          <button 
            onClick={() => setLocalModalOpen(true)}
            disabled={!canShowMoreOptions}
            className={`px-full py-3 border-none rounded-xl flex flex-col items-center justify-center text-center transition
              ${canShowMoreOptions ? 'bg-amber-200/50' : 'bg-gray-100 cursor-not-allowed text-gray-400'}`}
          >
            <Ellipsis className={`mb-0 ${canShowMoreOptions ? 'text-amber-500' : 'text-gray-400'} w-8 h-8`}/>
          </button>
          <span className='text-sm'>{t('components.quickActions.more')}</span>
          </div>
        </div>

        {/* More Options Modal */}
        {isModalOpen && (
          <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center ${getZIndexClass(Z_INDEX.BOTTOM_SHEET)}`}
               onClick={handleOverlayClick}>
            <div className={`bg-white rounded-t-xl w-full max-h-[100vh] overflow-y-auto
              ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`}>
              <div className="sticky top-0 bg-white px-4 pt-4 pb-2">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold">{t('components.quickActions.services')}</h3>
                </div>
              </div>
              
              <div className="p-0 mb-5">

                {/* skip delivery */}
                <div 
                  className={`px-4 py-3 flex items-center ${canSkip ? 'cursor-pointer hover:bg-amber-50' : 'cursor-not-allowed opacity-50'}`} 
                  onClick={canSkip ? onSkip : undefined}
                >
                  <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-4'>
                    <ChevronLast className='w-6 h-6 text-amber-500' />
                  </div>
                  <div className='flex flex-col flex-1'>
                    <span className='font-medium text-base'>{t('components.quickActions.skipDelivery.title')}</span>
                    <span className='text-xs text-gray-400'>{t('components.quickActions.skipDelivery.description')}</span>
                  </div>
                  <ChevronRight className='w-5 h-5 text-gray-400' />
                </div>

                {/* edit schedule */}
                <div 
                  className={`px-4 py-3 flex items-center ${canEditSchedule ? 'cursor-pointer hover:bg-amber-50' : 'cursor-not-allowed opacity-50'}`} 
                  onClick={canEditSchedule ? onEditSchedule : undefined}
                >
                  <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-4'>
                    <Calendar className='w-6 h-6 text-amber-500' />
                  </div>
                  <div className='flex flex-col flex-1'>
                    <span className='font-medium text-base'>{t('components.quickActions.editSchedule.title')}</span>
                    <span className='text-xs text-gray-400'>{t('components.quickActions.editSchedule.description')}</span>
                  </div>
                  <ChevronRight className='w-5 h-5 text-gray-400' />
                </div>
                
                {/* preferences - dynamic based on service type */}
                <div 
                  className={`px-4 py-3 flex items-center ${!isPendingRenewal ? 'cursor-pointer hover:bg-amber-50' : 'cursor-not-allowed opacity-50'}`} 
                  onClick={!isPendingRenewal ? handlePreferences : undefined}
                >
                  <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-4'>
                    <Flower className='w-6 h-6 text-amber-500' />
                  </div>
                  <div className='flex flex-col flex-1'>
                    <span className='font-medium text-base'>
                      {subscription.serviceType === 'fruit' 
                        ? t('components.quickActions.fruitPreferences.title', 'Fruit Preferences')
                        : t('components.quickActions.preferences.title', 'Flower Preferences')}
                    </span>
                    <span className='text-xs text-gray-400'>
                      {subscription.serviceType === 'fruit'
                        ? t('components.quickActions.fruitPreferences.description', 'Set your preferences for fruit selection')
                        : t('components.quickActions.preferences.description', 'Set your preferences for flower arrangements')}
                    </span>
                  </div>
                  <ChevronRight className='w-5 h-5 text-gray-400' />
                </div>
                
                {/* pause or resume */}
                {isPaused ? (
                  <div key={`modal-resume-${status}`} className='px-4 py-3 flex items-center cursor-pointer hover:bg-gray-50' onClick={onResume}>
                    <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-4'>
                      <Play className='w-6 h-6 text-amber-500' />
                    </div>
                    <div className='flex flex-col flex-1'>
                      <span className='font-medium text-base'>{t('components.quickActions.resumeSubscription.title')}</span>
                      <span className='text-xs text-gray-400'>{t('components.quickActions.resumeSubscription.description')}</span>
                    </div>
                    <ChevronRight className='w-5 h-5 text-gray-400' />
                  </div>
                ) : (
                  <div 
                    key={`modal-pause-${status}`} 
                    className={`px-4 py-3 flex items-center ${canPauseResume ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-50'}`} 
                    onClick={canPauseResume ? onPause : undefined}
                  >
                    <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-4'>
                      <Pause className='w-6 h-6 text-amber-500' />
                    </div>
                    <div className='flex flex-col flex-1'>
                      <span className='font-medium text-base'>{t('components.quickActions.pauseSubscription.title')}</span>
                      <span className='text-xs text-gray-400'>{t('components.quickActions.pauseSubscription.description')}</span>
                    </div>
                    <ChevronRight className='w-5 h-5 text-gray-400' />
                  </div>
                )}

                {activeSubscription?.status && activeSubscription.status !== 'cancelled' && (
                  <SubscriptionActions
                    subscription={subscription}
                    onUpgrade={onUpgrade}
                    onRenew={FEATURES.ENABLE_RENEWALS ? onRenew : undefined}
                    onCancel={onCancel}
                    onPause={onPause}
                    onResume={onResume}
                  />
                )}
              
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add PreferencesModal */}
      <PreferencesModal
        isOpen={showPreferencesModal}
        onClose={() => setShowPreferencesModal(false)}
        subscriptionId={subscription.id}
        serviceType={subscription.serviceType}
      />
    </>
  );
};

export default QuickActions; 